import { ChangeEvent, FC, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { IconButton } from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';

const VisuallyHiddenInput = styled.input<{ $disabled: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 1px;
  text-indent: -999px;
  opacity: 0;
  ${({ $disabled }) =>
    !$disabled &&
    css`
      cursor: pointer;
    `}
`;

const defaultAcceptedUploadFileTypes = 'image/*, video/*,';

interface Props {
  isDisabled: boolean;
  title: string;
  onMediaSubmit: (files: File[]) => void;
  acceptedUploadFileTypes?: string;
}

const FileUploadButton: FC<Props> = ({
  isDisabled,
  onMediaSubmit,
  title,
  acceptedUploadFileTypes,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    inputRef.current?.blur();

    const files: File[] = Array.from(event.target.files as ArrayLike<File>);
    onMediaSubmit(files);
    event.target.value = '';
  };

  const accept = useMemo(
    () => acceptedUploadFileTypes || defaultAcceptedUploadFileTypes,
    [acceptedUploadFileTypes]
  );

  return (
    <IconButton disabled={isDisabled} component="label" role={undefined}>
      <ImageOutlined />
      <VisuallyHiddenInput
        ref={inputRef}
        type="file"
        name="file-upload"
        title={title}
        accept={accept}
        onChange={handleChange}
        $disabled={isDisabled}
        multiple
      />
    </IconButton>
  );
};

export default FileUploadButton;
